import { IDetailsRowProps, MessageBarType } from '@fluentui/react';
import {
  debounce,
  defaultPalette,
  ISearchProps,
  Item,
  itemStylesTag,
  Link,
  ListRow,
  listRowStylesCustomRow,
  Search,
  SearchResultItemType,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  FeatureSearchResults,
  FeatureSetSearchResults,
  ListableFeature,
  ListableFeatureSet,
  ListableProject,
  ProjectSearchResults,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { RoutePaths } from '../../pages/Routes';

enum SearchResultType {
  all = 'All',
  project = 'Project',
  featureSet = 'Feature Set',
  feature = 'Feature',
}

const TypeStyleMap = new Map([
  [SearchResultType.all, { backgroundColor: defaultPalette.gray200, color: defaultPalette.gray900 }],
  [SearchResultType.project, { backgroundColor: defaultPalette.green100, color: defaultPalette.green900 }],
  [SearchResultType.featureSet, { backgroundColor: defaultPalette.blue100, color: defaultPalette.blue900 }],
  [SearchResultType.feature, { backgroundColor: '#FFEBD9', color: '#BA6010' }],
]);

const getSearchResultItemTypes = (selectedType: SearchResultType = SearchResultType.all): SearchResultItemType[] =>
  Object.values(SearchResultType).map((t) => ({ id: t, name: t, selected: t === selectedType }));

interface SearchResult {
  id?: string;
  featureSetId?: string;
  title?: string;
  description?: string;
  iconName?: string;
  type?: SearchResultType;
  isNotLoaded?: boolean;
}

interface SearchResultsData {
  items: SearchResult[];
  projectPageToken?: string;
  featureSetPageToken?: string;
  featurePageToken?: string;
}

const pageSize = 50;

const getDataAndMessages = (
  items: SearchResult[],
  projectResult: ProjectSearchResults | Error | null,
  featureSetResult: FeatureSetSearchResults | Error | null,
  featureResult: FeatureSearchResults | Error | null
) => {
  const messages: string[] = [];
  let startIndex = items.length ? items.length - pageSize : 0;
  let projectPageToken: string | undefined;
  let featureSetPageToken: string | undefined;
  let featurePageToken: string | undefined;
  let projects: ListableProject[] = [];
  let featureSets: ListableFeatureSet[] = [];
  let features: ListableFeature[] = [];

  if (projectResult) {
    if (projectResult instanceof Error) {
      messages.push(projectResult.message);
    } else {
      const { listableProject = [], nextPageToken } = projectResult;
      projectPageToken = nextPageToken;
      projects = listableProject;
    }
  }

  if (featureSetResult) {
    if (featureSetResult instanceof Error) {
      messages.push(featureSetResult.message);
    } else {
      const { listableFeatureSet = [], nextPageToken } = featureSetResult;
      featureSetPageToken = nextPageToken;
      featureSets = listableFeatureSet;
    }
  }

  if (featureResult) {
    if (featureResult instanceof Error) {
      messages.push(featureResult.message);
    } else {
      const { listableFeature = [], nextPageToken } = featureResult;
      featurePageToken = nextPageToken;
      features = listableFeature;
    }
  }

  const count = Math.max(projects.length, featureSets.length, features.length);
  for (let i = 0; i < count; i++) {
    const project = projects[i];
    const featureSet = featureSets[i];
    const feature = features[i];
    if (project) {
      items[startIndex] = {
        id: project.id,
        title: project.name,
        description: project.description,
        iconName: 'DocumentSet',
        type: SearchResultType.project,
      };
      startIndex++;
    }
    if (featureSet) {
      items[startIndex] = {
        id: featureSet.id,
        title: featureSet.name,
        description: featureSet.description,
        iconName: 'FormLibraryMirrored',
        type: SearchResultType.featureSet,
      };
      startIndex++;
    }
    if (feature) {
      items[startIndex] = {
        id: feature.featureId,
        featureSetId: feature.featureSetId,
        title: feature.name,
        description: feature.description,
        iconName: 'FormLibraryMirrored',
        type: SearchResultType.feature,
      };
      startIndex++;
    }
  }

  // add the page size of temp rows of the next page for preparing pagination - display the shimmer
  if (projectPageToken || featureSetPageToken || featurePageToken) {
    for (let i = 0; i < pageSize; i++) {
      const id = `${Date.now()}${startIndex}`;
      items.push({ id, isNotLoaded: true });
      startIndex++;
    }
  } else {
    items = items.slice(0, startIndex);
  }

  return { messages, items: [...items], projectPageToken, featureSetPageToken, featurePageToken };
};

const placeholderMessage = 'Please type at least 3 characters';

export function SearchAll() {
  const [searchText, setSearchText] = useState('');
  const [data, setData] = useState<SearchResultsData>({
    items: [],
  });
  const [loadingMessage, setLoadingMessage] = useState('');
  const history = useHistory();
  const refType = useRef<SearchResultType>(SearchResultType.all);
  const refIsLoadingPage = useRef(false);
  const refIsScrolled = useRef(false);
  const refSearchText = useRef('');
  const { addToast } = useToast();
  const load = useCallback(
    async (
      { items, projectPageToken, featureSetPageToken, featurePageToken }: SearchResultsData = { items: [] },
      isFirstPage = true
    ) => {
      const text = refSearchText.current;
      const type = refType.current;
      if (text.length < 3) {
        const data = {
          items: [],
        };
        setData({ ...data });
        return;
      }
      isFirstPage && setLoadingMessage(`Loading "${text}"...`);
      const results = await Promise.all(
        [
          (type === SearchResultType.all || type === SearchResultType.project) && (projectPageToken || isFirstPage)
            ? fsApi(CoreService.SearchProjects, {
                query: text,
                pageSize,
                pageToken: projectPageToken,
              })
            : new Promise((r) => r(null)),
          (type === SearchResultType.all || type === SearchResultType.featureSet) &&
          (featureSetPageToken || isFirstPage)
            ? fsApi(CoreService.SearchFeatureSets, {
                query: text,
                pageSize,
                pageToken: featureSetPageToken,
              })
            : new Promise((r) => r(null)),
          (type === SearchResultType.all || type === SearchResultType.feature) && (featurePageToken || isFirstPage)
            ? fsApi(CoreService.SearchFeatures, {
                query: text,
                pageSize,
                pageToken: featurePageToken,
              })
            : new Promise((r) => r(null)),
        ].map((p) => p.catch((e) => e))
      );
      const { messages, ...data } = getDataAndMessages(
        items,
        results[0] as ProjectSearchResults | Error | null,
        results[1] as FeatureSetSearchResults | Error | null,
        results[2] as FeatureSearchResults | Error | null
      );
      setData({ ...data });
      if (messages.length > 0) {
        addToast({
          messageBarType: MessageBarType.error,
          message: `An error has occurred while loading the search result: ${messages.join(', ')}`,
        });
      }
      if (isFirstPage) setLoadingMessage('');
      refIsLoadingPage.current = false;
    },
    [addToast]
  );
  const onLoad = useMemo(() => debounce(300, load), [load]);
  const searchProps: ISearchProps = {
    placeholder: placeholderMessage,
    emptyMessage: searchText.length > 2 ? 'No results found' : placeholderMessage,
    hasSearchResult: true,
    onSearchTextChange: (text) => {
      setSearchText(text);
      refSearchText.current = text;
      refIsScrolled.current = false;
      onLoad();
    },
    onClickItemType: (itemType) => {
      if (!itemType.selected) {
        refType.current = itemType.id as SearchResultType;
        // debounce should work only when scrolling.
        refIsScrolled.current = false;
        load();
      }
    },
    loadingMessage: loadingMessage,
    searchResultItemTypes: getSearchResultItemTypes(refType.current),
    searchResultItems: data.items,
    detailsListProps: {
      onRenderRow: (props?: IDetailsRowProps) => {
        const { item, columns = [] } = props || {};
        if (item.isNotLoaded && !refIsLoadingPage.current) {
          refIsLoadingPage.current = true;
          if (refIsScrolled.current) {
            onLoad(data, false);
          } else {
            load(data, false);
          }
        }
        return (
          <ListRow
            styles={listRowStylesCustomRow}
            hideHeader
            data={item}
            columns={columns}
            loading={item.isNotLoaded}
          />
        );
      },
    },
    onClickItem: (item: SearchResult) => {
      const { id, type, title, featureSetId } = item;
      switch (type) {
        case SearchResultType.project:
          history.push(`${RoutePaths.FEATURESTORE_PROJECT}/${id}?name=${title}`);
          break;
        case SearchResultType.featureSet:
          history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${id}`);
          break;
        case SearchResultType.feature:
          history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${featureSetId}/${id}`);
          break;
      }
    },
    searchResultItemFields: {
      descriptionField: 'description',
      iconNameField: 'iconName',
    },
    onRenderSearchResultItemActions: (item: SearchResult) => (
      <Item
        styles={itemStylesTag}
        labelField="type"
        styleField="style"
        data={{ ...item, style: item.type ? TypeStyleMap.get(item.type) : undefined }}
      />
    ),
    calloutProps: { alignTargetEdge: true },
    onScrollDetailsList: () => {
      if (refIsScrolled.current) return;
      refIsScrolled.current = true;
    },
    actions: (
      <Link
        style={{ color: defaultPalette.gray500 }}
        onClick={() => history.push(`${RoutePaths.FEATURESTORE_SEARCH_RESULTS}?query=${searchText}`)}
      >
        View all
      </Link>
    ),
  };

  return <Search {...searchProps} style={{ maxWidth: 641 }} />;
}
